/**
 * Resolves the base URL of the camera microservice used by the OEM tray assembly
 * flow (`/preview/start`, `/preview/stop`, `/snapshot`).
 *
 * Priority: runtime config (`window.__RUNTIME_CONFIG__`) → build-time env → localhost
 * default. Set `VITE_CAMERA_API_URL` to point at a different host.
 */

interface RuntimeConfig {
  VITE_CAMERA_API_URL?: string;
}

interface WindowWithRuntimeConfig {
  __RUNTIME_CONFIG__?: RuntimeConfig;
}

interface GlobalWithImportMetaEnv {
  import?: {
    meta?: {
      env?: RuntimeConfig;
    };
  };
}

/** Default local-dev address of the camera microservice. */
export const DEFAULT_CAMERA_API_URL = 'http://localhost:8002';

/** Resolves the camera API base URL from runtime config or environment variables. */
export const getCameraApiUrl = (): string => {
  if (typeof window !== 'undefined') {
    const runtimeValue = (window as WindowWithRuntimeConfig).__RUNTIME_CONFIG__
      ?.VITE_CAMERA_API_URL;
    // Make sure it's not still a template placeholder AND not empty.
    if (
      runtimeValue &&
      runtimeValue !== '${VITE_CAMERA_API_URL}' &&
      runtimeValue.trim() !== ''
    ) {
      return runtimeValue;
    }
  }

  // Read build-time env without using import.meta syntax so Jest can compile it.
  const viteValue = (globalThis as GlobalWithImportMetaEnv).import?.meta?.env
    ?.VITE_CAMERA_API_URL;

  if (typeof viteValue === 'string' && viteValue.trim() !== '') {
    return viteValue;
  }

  if (typeof process !== 'undefined') {
    const processValue = process.env?.['VITE_CAMERA_API_URL'];

    if (typeof processValue === 'string' && processValue.trim() !== '') {
      return processValue;
    }
  }

  return DEFAULT_CAMERA_API_URL;
};
